import React, { FunctionComponent, useMemo } from 'react'
import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import useIntl from '@/utils/intl'
import { Obj } from '..'
import { useAuth } from '@/context/AuthContext'
import { useRouter } from 'next/navigation'
import { IMG_URL } from '@/utils/constants'

interface Props {
  onClick: (objs: Obj) => void
  danRedirectUrl: string
  errorType?: 'BLACKLIST' | 'UNDERAGE' | 'CORP_ERROR'
  errorMessage?: string
  back: () => void
  onContinue?: () => void
}

type ErrorType = 'BLACKLIST' | 'UNDERAGE' | 'CORP_ERROR'

const isValidErrorType = (value: string): value is ErrorType => {
  return ['BLACKLIST', 'UNDERAGE', 'CORP_ERROR'].includes(value)
}

const EMongolia: FunctionComponent<Props> = ({
  onClick,
  danRedirectUrl,
  errorType,
  errorMessage,
  back
}) => {
  const { translate } = useIntl()
  const { isWebView } = useAuth()
  const router = useRouter()

  const derivedErrorType: ErrorType | undefined = useMemo(() => {
    const normalizedMessage = (errorMessage ?? '').toUpperCase().trim()
    if (isValidErrorType(normalizedMessage)) {
      return normalizedMessage
    }
    return errorType
  }, [errorMessage, errorType])

  const content = derivedErrorType
    ? {
        BLACKLIST: {
          image: IMG_URL + '/uploads/icons/handset_fail_web.svg',
          title: translate('leasing.blacklist.title'),
          description: translate('leasing.blacklist.description')
        },
        UNDERAGE: {
          image: IMG_URL + '/uploads/icons/handset_fail_web.svg',
          title: translate('leasing.underage.title'),
          description: translate('leasing.underage.description')
        },
        CORP_ERROR: {
          image: IMG_URL + '/uploads/icons/handset_fail_web.svg',
          title: translate('leasing.corpError.title'),
          description: translate('leasing.corpError.description')
        }
      }[derivedErrorType]
    : {
        image: IMG_URL + '/uploads/icons/mobot.svg',
        title: translate('leasing.eMongoliaconfirm'),
        description: translate('leasing.eMongoliaText')
      }

  const isButtonDisabled = !derivedErrorType && danRedirectUrl === ''

  const exitWebview = (): void => {
    let webView
    if ((window as any).mobiBridge !== undefined) {
      webView = (window as any).mobiBridge
    }
    if ((window as any).ReactNativeWebview !== undefined) {
      webView = (window as any).ReactNativeWebview
    }
    if (webView !== undefined) {
      const data = { key: 'exit' }
      webView.postMessage(JSON.stringify(data))
    } else {
      router.push('/')
    }
  }

  const handleContinue = (): void => {
    if (danRedirectUrl === undefined || danRedirectUrl === null) {
      alert(translate('leasing.dan_url_missing'))
      exitWebview()
      return
    }
    if (derivedErrorType !== undefined || derivedErrorType === null) {
      exitWebview()
      return
    }

    const returnUrl = encodeURIComponent(
    `${window.location.origin}${window.location.pathname}?step=2&externalSuccess=true`
    )
    const redirectUrlWithReturn =
    danRedirectUrl +
    (danRedirectUrl.includes('?') ? '&' : '?') +
    `returnUrl=${returnUrl}`

    onClick({ danUrl: danRedirectUrl })
    router.push(redirectUrlWithReturn)
  }

  return (
    <div
      className={`bg-surfaceInverse rounded-xl md:shadow w-full h-full ${
        derivedErrorType ? 'md:h-[40vh]' : 'md:h-[55vh]'
      } md:w-fit flex flex-col ${
        isWebView ? 'min-h-screen justify-center' : 'justify-start'
      } relative no-scrollbar`}
    >
      <div className='flex-grow px-4 py-4 sm:px-8 sm:py-8 overflow-auto no-scrollbar flex flex-col gap-4 mt-40 sm:mt-0'>
        <div className='flex items-center justify-center'>
          <img
            src={content.image}
            className={
              derivedErrorType ? 'w-[64px] h-[64px]' : 'w-[200px] h-[200px]'
            }
            alt={derivedErrorType ? derivedErrorType.toLowerCase() : 'mobot'}
          />
        </div>
        <span className='text-textHigh font-extrabold text-lg text-center'>
          {content.title}
        </span>
        <span className='text-textMid text-sm text-center whitespace-pre-line line-clamp-4'>
          {content.description}
        </span>
      </div>
      <div className='fixed md:sticky bottom-0 rounded-b-xl right-0 left-0 z-50 md:p-6 p-4 border-t bg-surfaceInverse border-secondaryMidStroke'>
        <div className='md:w-96 w-full flex gap-2'>
          <DarkButton
            fullwidth
            onClick={handleContinue}
            aria-label={translate('confirm')}
            disabled={isButtonDisabled}
          >
            <span className='line-clamp-1'>
              {derivedErrorType
                ? translate('leasing.fail')
                : translate('confirm')}
            </span>
          </DarkButton>
        </div>
      </div>
    </div>
  )
}

export default EMongolia
